import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { SubmitPromise } from "@saleor/hooks/useForm";
import useUser from "@saleor/hooks/useUser";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

import { TenantList_tenantsPrivate_edges_node } from "../../types/TenantList";
import StoreFeaturedStar from "../StoreFeaturedStar";

const useStyles = makeStyles(
  _ => ({
    colFeatured: {
      width: 170
    },
    colName: {
      paddingLeft: 0
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "StoreList" }
);

export interface StoreListProps extends ListProps {
  /** Data */
  tenants?: TenantList_tenantsPrivate_edges_node[];

  /** Custom Event handlers */
  onSubmit?: (data) => SubmitPromise;

  /** Permissions */
  hasManageTenantPermission: boolean;
}

const StoreList: React.FC<StoreListProps> = props => {
  const {
    /** Data */
    tenants,

    /** Event handlers */
    onSubmit,

    /** Permissions */
    hasManageTenantPermission,

    /** List controls */
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick
  } = props;
  const userType = useUser();
  const classes = useStyles(props);
  const storeAccess =
    userType?.user?.userType?.code === "OMI_ADMIN" ||
    userType?.user?.userType?.code === "ACQUISITION" ||
    userType?.user?.userType?.code === "" ||
    userType?.user?.userType?.code === "MARKETING";

  const numberOfColumns = storeAccess ? 7 : 6;

  const SmopStatus = {
    ACT: "Active",
    DCL: "Declined",
    DEL: "Deleted",
    INA: "Inactive",
    RVW: "Reviewed"
  };

  return (
    <ResponsiveTable>
      <TableHead colSpan={numberOfColumns} disabled={disabled} items={[]}>
        {!storeAccess ? null : (
          <TableCellHeader className={classes.colFeatured}>
            <FormattedMessage defaultMessage="Featured" />
          </TableCellHeader>
        )}
        <TableCellHeader className={classes.colName}>
          <FormattedMessage defaultMessage="Store ID" />
        </TableCellHeader>
        <TableCellHeader className={classes.colName}>
          <FormattedMessage
            defaultMessage="Mall"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName}>
          <FormattedMessage
            defaultMessage="(Seller) Company Name"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName}>
          <FormattedMessage
            defaultMessage="Brand Name"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName}>
          <FormattedMessage
            defaultMessage="Contract ID"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName}>
          <FormattedMessage defaultMessage="Status" description="status" />
        </TableCellHeader>
      </TableHead>

      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>

      <TableBody>
        {renderCollection(
          tenants,
          (tenant, index) => (
            <TableRow
              className={classes.tableRow}
              hover={!!tenant}
              key={tenant ? tenant.id : "skeleton"}
              data-test="id"
              data-test-id={maybe(() => tenant.id)}
              onClick={tenant ? onRowClick(tenant.id) : undefined}
            >
              {!storeAccess ? null : (
                <TableCell
                  className={classes.colName}
                  data-test="isFeatured"
                  onClick={e => e.stopPropagation()}
                >
                  <div onClick={e => e.stopPropagation()}>
                    <StoreFeaturedStar
                      id={tenant.id}
                      isFeatured={tenant.isFeatured}
                      onSubmit={onSubmit}
                      hasManageTenantPermission={hasManageTenantPermission}
                    />
                  </div>
                </TableCell>
              )}
              <TableCell
                className={classes.colName}
                data-test="storeId"
                id={`storeIDRow${index}`}
              >
                {tenant && tenant.storeId ? tenant.storeId : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colName}
                data-test="mallName"
                id={`mallNameRow${index}`}
              >
                {tenant && tenant.mall.name ? tenant.mall.name : <Skeleton />}
              </TableCell>
              <TableCell className={classes.colName} data-test="storeName">
                {tenant && tenant.storeName ? (
                  tenant?.brand?.legalName
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell className={classes.colName} data-test="brandName">
                {tenant && tenant.brand.name ? tenant.brand.name : <Skeleton />}
              </TableCell>
              <TableCell className={classes.colName} data-test="contractId">
                {tenant && tenant.contractId ? tenant.contractId : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colName}
                data-test="status"
                id={`statusRow${index}`}
              >
                {maybe(() => SmopStatus[tenant.status], "-")}
              </TableCell>
            </TableRow>
          ),
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No store found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

StoreList.displayName = "StoreList";
export default StoreList;
